<template>
  <mailbox-settings-layout :id="id" :name="name">
    <el-card class="mailbox-settings-checklist-templates">
      <template #header>
        <h3>Checklist Templates</h3>
      </template>
      <entity-settings-loading v-if="isLoading" />
      <template v-else>
        <p>
          Create re-usable templates for your checklists. 
          Checklist Templates can be accessed from the add
          checklist button on a task.
        </p>
        <div class="dd-divider" />
        <div class="row">
          <div class="col-12 templates-list">
            <div class="templates-list__header">
              <h4>Templates</h4>
              <dd-icon-button 
                :is-disabled="isSubmitting" 
                icon="plus" 
                type="circle" 
                size="medium" 
                title="Add saved template"
                @click="beginCreateTemplate()" 
              />
            </div>
            <div v-if="hasDescTemplates">
              <div v-for="checklistTemplate in checklistTemplates" :key="checklistTemplate.id" class="mailbox-settings-checklist-templates-list">
                <i class="ddi ddi-everything" />
                <span class="mailbox-settings-checklist-templates-list__title">{{ checklistTemplate.title }}</span>
                <router-link
                  v-if="getStatusByID(checklistTemplate.statusID)"
                  :to="RouteAssistant.getMailboxSettingsWorkflowAutomationsRoute($route.params.accountId, id)"
                  class="mailbox-settings-checklist-templates-list__status"
                >
                  <status-chicklet :status="getStatusByID(checklistTemplate.statusID)" />
                </router-link>
                <div class="mailbox-settings-checklist-templates-list__actions">
                  <dd-icon-button 
                    :is-disabled="isSubmitting" 
                    icon="edit"
                    type="rounded"
                    size="medium"
                    color="grey"
                    @click="beginEditTemplate(checklistTemplate.id)" 
                  />
                  <dd-icon-button 
                    :is-disabled="isSubmitting" 
                    danger
                    icon="trash"
                    type="rounded"
                    size="medium"
                    color="grey"
                    @click="removeTemplate(checklistTemplate.id)" 
                  />
                </div>
              </div>
            </div>
            <p v-else class="templates-list__empty-message">
              There are no checklist templates for this project. 
              <a href="#" class="just-a-link" @click.stop.prevent="beginCreateTemplate()">Add your first</a>!
            </p>
          </div>
        </div>
        <!-- footer -->
        
        <!-- Modal -->
        <dd-modal
          v-model:visible="modalOpened"
          :title="(isEditing)? `Edit Template` : `New Template`"
          @handle-close="closeModal"
        >
          <el-form ref="editTemplateForm" :model="editingTemplate" :rules="rules" label-position="top">
            <!-- Title -->
            <el-form-item label="Title" prop="title">
              <el-input
                ref="savedTemplateForm_Title"
                v-model="editingTemplate.title"
                placeholder="e.g. Checklist Template"
                maxlength="100"
              />
            </el-form-item>
            <label class="el-form-item__label">Checklist Items</label>
            <draggable
              :animation="180"
              :scroll-sensitivity="100"
              :scroll-speed="20"
              :force-fallback="true"
              :list="editingTemplate.checklistTemplateItems"
              :handle="'.drag-handle'"
              drag-class="drag-card"
              item-key="sort"
            >
              <template #item="{ element: item, index }">
                <el-form-item>
                  <div class="input-with-trash">
                    <el-input
                      v-model="item.name"
                      :autosize="{ minRows: 1 }"
                      placeholder="e.g. Item To Complete"
                      maxlength="150"
                      type="textarea"
                      resize="none"
                      @keydown.enter="preventLineBreak"
                    />
                    <span class="drag-handle" @mousedown.prevent><i class="ddi ddi-sort" /></span>
                    <i class="ddi ddi-trash scary-list-option" @click="editingTemplate.checklistTemplateItems.splice(index, 1)" />
                  </div>
                </el-form-item>
              </template>
            </draggable>
            <dd-button
              type="text"
              @click="editingTemplate.checklistTemplateItems.push({ id: 0, name: '', sort: editingTemplate.checklistTemplateItems.length })">
              + Add item
            </dd-button>
          </el-form>
          <template #footer>
            <span>
              <dd-button type="text" @click="closeModal">Cancel</dd-button>
              <dd-button :color="ColorAssistant.ProjectColor" @click="saveTemplate">{{ (isEditing)? `Update Template` : `Add Template` }}</dd-button>
            </span>
          </template>
        </dd-modal>
      </template>
    </el-card>
  </mailbox-settings-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import MailboxSettingsLayout from '@/layouts/mailbox-settings-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'
import draggable from 'vuedraggable'

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'
import RouteAssistant from '@/helpers/route-assistant'

/* Data types */

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'
import WorkflowAPI from '@/api/modules/workflows.api'
import {
  IChecklistTemplate,
  IStatusForSelection,
} from '@/api/types/response.types'

export default defineComponent({
  name: 'MailboxSettingsChecklistTemplates',

  components: {
    MailboxSettingsLayout,
    EntitySettingsLoading,
    draggable,
  },

  data() {
    return {
      ColorAssistant,
      RouteAssistant,
      isLoading: true,
      isSubmitting: false,
      editingID: null as number,
      name: '',
      checklistTemplates: [] as IChecklistTemplate[],
      editingTemplate: {} as IChecklistTemplate,
      listStatuses: [] as IStatusForSelection[],
      modalOpened: false,
      rules: {
        title: [
          {
            required: true,
            trigger: 'submit',
            message: 'Title is required',
          },
        ],
        template: [
          {
            required: true,
            trigger: 'submit',
            message: 'Template is required',
          },
        ],
      },
    }
  },

  head(): any {
    return {
      title: `Checklist Templates - ${this.name}`,
    }
  },

  computed: {
    id(): number {
      return parseInt(this.$route.params.id, 10)
    },
    isEditing(): boolean {
      return this.editingID != null
    },
    hasDescTemplates(): boolean {
      return this.checklistTemplates.length > 0
    },
  },

  watch: {
    modalOpened(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          // @ts-ignore
          this.$refs.savedTemplateForm_Title.focus()
        })
      }
    },
  },

  async created() {
    this.isLoading = true
    await this.getTemplates()
    this.isLoading = false
  },

  methods: {
    async getTemplates() {
      try {
        const projectInfoResponse = await MailboxesAPI.get_WithWorkflow(this.id)
        this.name = projectInfoResponse.title
        this.checklistTemplates = await MailboxesAPI.get_ChecklistTemplates(
          this.id,
        )
        this.checklistTemplates.forEach(template => {
          if (template.checklistTemplateItems) {
            template.checklistTemplateItems.sort((a, b) => a.sort - b.sort)
          }
        })
        const workflowStatusList = await WorkflowAPI.get_Statuses(
          projectInfoResponse.workflowID,
        )
        this.listStatuses = workflowStatusList
      } catch (error) {
        this.$error.emit(error as any)
      }
    },
    async saveTemplate() {
      // @ts-ignore
      this.$refs.editTemplateForm.validate((valid: boolean) => {
        if (valid) {
          this.isSubmitting = true
          this.editingTemplate.checklistTemplateItems.forEach((item, index) => {
            item.sort = index
            // A pasted line break would otherwise end up in the name
            item.name = item.name.replace(/[\r\n]+/g, ' ')
          })
          try {
            if (this.isEditing) {
              const respone = MailboxesAPI.put_ChecklistTemplateUpdate(
                this.id,
                this.editingID,
                { checklistTemplate: this.editingTemplate },
              )
            } else {
              const respone = MailboxesAPI.post_ChecklistTemplateCreate(
                this.id,
                { checklistTemplate: this.editingTemplate },
              )
            }
          } catch (error) {
            this.$error.emit(error as any, true)
          } finally {
            this.isSubmitting = false
          }
          this.closeModal()
        }
      })
    },

    beginEditTemplate(id: number): void {
      this.editingTemplate = this.checklistTemplates.find(dt => dt.id === id)
      this.modalOpened = true
      this.editingID = id
    },

    beginCreateTemplate(): void {
      this.editingTemplate = {
        checklistTemplateItems: [],
      } as IChecklistTemplate
      this.modalOpened = true
      this.editingID = null
    },

    preventLineBreak(event: Event | KeyboardEvent): void {
      // Item names are one line. keyCode 229 is Enter confirming IME composition, which has to go through.
      if ((event as KeyboardEvent).keyCode !== 229) {
        event.preventDefault()
      }
    },

    async removeTemplate(id: number) {
      await this.$confirm(
        `Are you sure you want to delete this template?<br />
         This cannot be undone.`,
        {
          dangerouslyUseHTMLString: true,
          center: true,
          confirmButtonText: 'Yes, Delete',
          confirmButtonClass: 'el-button--strawberry',
          cancelButtonText: 'Cancel',
          cancelButtonClass: 'el-button--text',
          type: 'warning',
          title: `Delete Checklist Template`,
        },
      )
      await this.deleteChecklistTemplate(id)
    },

    closeModal(): void {
      this.getTemplates()
      this.modalOpened = false
      // @ts-ignore
      this.$refs.editTemplateForm.clearValidate()
    },

    async deleteChecklistTemplate(id: number) {
      try {
        this.isSubmitting = true
        const response = await MailboxesAPI.delete_ChecklistTemplateDelete(
          this.id,
          id,
        )
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error as any, true)
      } finally {
        await this.getTemplates()
        this.isSubmitting = false
      }
    },

    getStatusByID(statusID: number | null): IStatusForSelection {
      return this.listStatuses.find(status => status.id === statusID)
    },
  },
})
</script>

<style lang="scss">
.mailbox-settings-checklist-templates {
  .in-text {
    font-size: 14px;
    padding: 0 2px;
  }

  .templates-list {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
    }

    &__empty-message {
      color: var(--color-text-third);
      padding-bottom: 20px;
    }
  }
}
.mailbox-settings-checklist-templates-list {
  display: flex;
  align-items: center;
  padding: 12px 0;

  &:not(:last-child) {
    border-bottom: $border--solid-extra-light;
  }

  > i {
    color: var(--color-text-third);
  }

  &__title {
    margin-left: 10px;
    @include lettering--p;
  }

  &__status {
    margin-left: 10px;

    &:hover {
      text-decoration: none;
    }
  }

  &__actions {
    display: flex;
    margin-left: auto;

    > :last-child {
      margin-left: 8px;
    }
  }
}
.input-with-trash {
  display: flex;
  align-items: center;

  .el-input,
  .el-textarea {
    flex: 1;
  }

  /* Matches the single-line inputs: one line is 40px tall, longer names wrap */
  .el-textarea__inner {
    padding: 8px 12px;
    font-size: 15px;
    line-height: 22px;
  }

  .ddi-trash {
    margin-left: 8px;
    cursor: pointer;
    font-size: 18px;
  }

  .drag-handle {
    margin-left: 8px;
    cursor: grab;
    color: var(--color-text-third);
    font-size: 16px;
  }
}

.drag-card {
  cursor: grab !important;
  opacity: 0.95 !important;
  background-color: var(--color-background-primary) !important;
  border: solid 1px $light-blue-grey-two;
}
</style>
